/**
 * FTUEAudioPlayer — plays mp3 files and exposes audio levels for VoiceOrb reactivity.
 *
 * Web-only. Uses Web Audio API to decode and play mp3, routing through an
 * AnalyserNode so getAudioLevel() returns real-time output levels that the
 * VoiceOrb animation loop picks up.
 */

let audioContext: AudioContext | null = null;
let analyserNode: AnalyserNode | null = null;
let analyserData: Uint8Array | null = null;
let currentSource: AudioBufferSourceNode | null = null;
let abortController: AbortController | null = null;

function ensureContext() {
  if (!audioContext) {
    audioContext = new AudioContext({ sampleRate: 44100 });
    analyserNode = audioContext.createAnalyser();
    analyserNode.fftSize = 256;
    analyserNode.smoothingTimeConstant = 0.4;
    analyserData = new Uint8Array(analyserNode.frequencyBinCount);
    analyserNode.connect(audioContext.destination);
  }
  if (audioContext.state === "suspended") audioContext.resume();
}

export function getAudioLevel(): number {
  if (!analyserNode || !analyserData) return 0;
  analyserNode.getByteFrequencyData(analyserData);
  let sum = 0;
  for (let i = 0; i < analyserData.length; i++) sum += analyserData[i];
  return sum / analyserData.length / 255;
}

export async function playMp3(url: string): Promise<void> {
  ensureContext();
  stop();
  abortController?.abort();
  abortController = new AbortController();
  const { signal } = abortController;
  const response = await fetch(url, { signal });
  const arrayBuffer = await response.arrayBuffer();
  if (signal.aborted) return;
  const audioBuffer = await audioContext!.decodeAudioData(arrayBuffer);
  if (signal.aborted) return;
  const source = audioContext!.createBufferSource();
  source.buffer = audioBuffer;
  source.connect(analyserNode!);
  source.start();
  currentSource = source;
  return new Promise((resolve) => {
    source.onended = () => {
      if (currentSource === source) currentSource = null;
      resolve();
    };
  });
}

export function stop() {
  abortController?.abort();
  if (currentSource) {
    try { currentSource.stop(); } catch (_) {}
    currentSource = null;
  }
}

export function pause() {
  if (audioContext && audioContext.state === "running") audioContext.suspend();
}

export function resume() {
  if (audioContext && audioContext.state === "suspended") audioContext.resume();
}

export function dispose() {
  stop();
  if (audioContext) {
    audioContext.close().catch(() => {});
    audioContext = null;
    analyserNode = null;
    analyserData = null;
  }
}
